import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from '@/components/ui/use-toast';
import { Loader2, Search, RefreshCw, AlertCircle, User, UserPlus, Trash2, Edit, Mail, Shield, Calendar } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { DatePicker } from '@/components/ui/date-picker';

type UserData = {
  id: string;
  email: string;
  username?: string;
  role?: 'admin' | 'user';
  created_at?: string;
  last_sign_in_at?: string;
  subscription_status?: string;
  provider?: string;
};

type InviteData = {
  email: string;
  expires_at?: Date;
  role: 'admin' | 'user';
};

const UserManagement: React.FC = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState<UserData[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isInviteDialogOpen, setIsInviteDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<UserData | null>(null);
  const [newUserData, setNewUserData] = useState<{ email: string; password: string; username: string; role: 'admin' | 'user' }>({
    email: '',
    password: '',
    username: '',
    role: 'user'
  });
  const [inviteFormData, setInviteFormData] = useState<InviteData>({
    email: '',
    role: 'user'
  });
  const [hasExpiryDate, setHasExpiryDate] = useState(false);
  const [expiryDate, setExpiryDate] = useState<Date | undefined>(undefined);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      
      // First get users from the profiles table
      const { data: profilesData, error: profilesError } = await supabase
        .from('profiles')
        .select('*');

      if (profilesError) {
        console.error('Error fetching profiles:', profilesError);
        throw profilesError;
      }

      console.log('Profiles data:', profilesData);
      
      // Then try to get auth users through our edge function
      const { data: authUsersData, error: authUsersError } = await supabase.functions.invoke('get-all-users', {
        body: {},
      });
      
      if (authUsersError) {
        console.error('Error fetching auth users:', authUsersError);
        // Continue with profiles data if auth fails
      } else {
        console.log('Auth users data:', authUsersData);
      }
      
      // Combine the data, prioritizing auth data but augmenting with profile data
      const combinedUsers: UserData[] = [];
      
      // If we have auth data, start with that
      if (authUsersData?.users && Array.isArray(authUsersData.users)) {
        authUsersData.users.forEach((authUser: any) => {
          const profileMatch = profilesData?.find(p => p.id === authUser.id);
          
          combinedUsers.push({
            id: authUser.id,
            email: authUser.email || 'Unknown',
            username: profileMatch?.username || authUser.user_metadata?.full_name || 'Unknown',
            role: profileMatch?.role || 'user',
            created_at: authUser.created_at,
            last_sign_in_at: authUser.last_sign_in_at,
            subscription_status: profileMatch?.subscription_status,
            provider: authUser.app_metadata?.provider || 'email'
          });
        });
      } 
      // If no auth data, use profile data
      else if (profilesData && profilesData.length > 0) {
        profilesData.forEach(profile => {
          if (!combinedUsers.some(u => u.id === profile.id)) {
            combinedUsers.push({
              id: profile.id,
              email: profile.email || 'Unknown',
              username: profile.username || 'Unknown',
              role: profile.role || 'user',
              created_at: profile.created_at,
              subscription_status: profile.subscription_status
            });
          }
        });
      }
      
      setUsers(combinedUsers);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast({
        title: 'Error',
        description: 'Failed to load user data',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const filteredUsers = users.filter(user => {
    const searchLower = searchTerm.toLowerCase();
    return (
      user.email.toLowerCase().includes(searchLower) ||
      (user.username && user.username.toLowerCase().includes(searchLower)) ||
      (user.role && user.role.toLowerCase().includes(searchLower))
    );
  });

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  const handleAddUser = async () => {
    try {
      // Basic validation
      if (!newUserData.email || !newUserData.password) {
        toast({
          title: 'Error',
          description: 'Email and password are required',
          variant: 'destructive',
        });
        return;
      }
      
      setLoading(true);
      
      // First create the auth user
      const { data: authData, error: authError } = await supabase.auth.admin.createUser({
        email: newUserData.email,
        password: newUserData.password,
        email_confirm: true,
      });
      
      if (authError) {
        throw authError;
      }
      
      const userId = authData.user.id;
      
      // Then create or update the profile
      const { error: profileError } = await supabase
        .from('profiles')
        .upsert({
          id: userId,
          email: newUserData.email,
          username: newUserData.username,
          role: newUserData.role,
          updated_at: new Date().toISOString()
        });
        
      if (profileError) {
        throw profileError;
      }
      
      toast({
        title: 'Success',
        description: 'User created successfully',
      });
      
      // Reset form and close dialog
      setNewUserData({
        email: '',
        password: '',
        username: '',
        role: 'user'
      });
      setIsAddDialogOpen(false);
      
      // Refresh user list
      fetchUsers();
    } catch (error: any) {
      console.error('Error creating user:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to create user',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEditUser = async () => {
    if (!selectedUser) return;
    
    try {
      setLoading(true);
      
      // Update the profile
      const { error: profileError } = await supabase
        .from('profiles')
        .update({
          username: selectedUser.username,
          role: selectedUser.role,
          updated_at: new Date().toISOString()
        })
        .eq('id', selectedUser.id);
        
      if (profileError) {
        throw profileError;
      }
      
      toast({
        title: 'Success',
        description: 'User updated successfully',
      });
      
      setIsEditDialogOpen(false);
      
      // Refresh user list
      fetchUsers();
    } catch (error: any) {
      console.error('Error updating user:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to update user',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteUser = async () => {
    if (!selectedUser) return;
    
    try {
      setLoading(true);
      
      // Delete the user from auth
      const { error: authError } = await supabase.auth.admin.deleteUser(selectedUser.id);
      
      if (authError) {
        throw authError;
      }
      
      // Delete the profile
      const { error: profileError } = await supabase
        .from('profiles')
        .delete()
        .eq('id', selectedUser.id);
        
      if (profileError) {
        console.error('Error deleting profile, but user auth deleted:', profileError);
        // Continue since the auth user is deleted
      }
      
      toast({
        title: 'Success',
        description: 'User deleted successfully',
      });
      
      setIsDeleteDialogOpen(false);
      
      // Refresh user list
      fetchUsers();
    } catch (error: any) {
      console.error('Error deleting user:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to delete user',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleInviteUser = async () => {
    try {
      // Basic validation
      if (!inviteFormData.email) {
        toast({
          title: 'Error',
          description: 'Email is required',
          variant: 'destructive',
        });
        return;
      }
      
      setLoading(true);
      
      // Create the invite using the edge function
      const { data: inviteResponseData, error: inviteError } = await supabase.functions.invoke('create-invite', {
        body: { 
          email: inviteFormData.email,
          role: inviteFormData.role,
          expires_at: hasExpiryDate && expiryDate ? expiryDate.toISOString() : undefined
        },
      });
      
      if (inviteError) {
        throw inviteError;
      }
      
      toast({
        title: 'Success',
        description: 'Invitation sent successfully',
      });
      
      // Reset form and close dialog
      setInviteFormData({
        email: '',
        role: 'user'
      });
      setHasExpiryDate(false);
      setExpiryDate(undefined);
      setIsInviteDialogOpen(false);
    } catch (error: any) {
      console.error('Error sending invitation:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to send invitation',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const getRoleBadgeVariant = (role?: string) => {
    switch (role) {
      case 'admin':
        return 'default';
      case 'user':
        return 'secondary';
      default:
        return 'outline';
    }
  };

  const getSubscriptionBadgeVariant = (status?: string) => {
    switch (status) {
      case 'active':
        return 'default';
      case 'trial':
        return 'secondary';
      case 'canceled':
        return 'destructive';
      case 'past_due':
        return 'outline';
      default:
        return 'outline';
    }
  };

  const openEditDialog = (user: UserData) => {
    setSelectedUser(user);
    setIsEditDialogOpen(true);
  };

  const openDeleteDialog = (user: UserData) => {
    setSelectedUser(user);
    setIsDeleteDialogOpen(true);
  };

  const handleViewUserDetails = (userId: string) => {
    navigate(`/admin/users/${userId}`);
  };
  
  return (
    <div className="container mx-auto py-10 space-y-6">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold">User Management</h2>
          <p className="text-muted-foreground">Manage users, roles, and invitations</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchUsers} disabled={loading}>
            {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />}
            Refresh
          </Button>
          
          <Button variant="default" onClick={() => setIsAddDialogOpen(true)}>
            <User className="mr-2 h-4 w-4" />
            Add User
          </Button>
          
          <Button variant="secondary" onClick={() => setIsInviteDialogOpen(true)}>
            <Mail className="mr-2 h-4 w-4" />
            Invite User
          </Button>
        </div>
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search by email, name, or role..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10"
        />
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Users</CardTitle>
          <CardDescription>
            {filteredUsers.length} user{filteredUsers.length !== 1 ? 's' : ''} found
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : filteredUsers.length > 0 ? (
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>User</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead>Last Sign In</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredUsers.map((user) => (
                    <TableRow 
                      key={user.id} 
                      className="cursor-pointer hover:bg-muted/50"
                      onClick={() => handleViewUserDetails(user.id)}
                    >
                      <TableCell>
                        <div className="font-medium">{user.username || 'No Username'}</div>
                        <div className="text-sm text-muted-foreground">{user.email}</div>
                        {user.provider && user.provider !== 'email' && (
                          <Badge variant="outline" className="mt-1">
                            {user.provider}
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge variant={getRoleBadgeVariant(user.role)}>
                          {user.role || 'user'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {user.subscription_status ? (
                          <Badge variant={getSubscriptionBadgeVariant(user.subscription_status)}>
                            {user.subscription_status}
                          </Badge>
                        ) : (
                          <span className="text-muted-foreground">No subscription</span>
                        )}
                      </TableCell>
                      <TableCell>{formatDate(user.created_at)}</TableCell>
                      <TableCell>{user.last_sign_in_at ? formatDate(user.last_sign_in_at) : 'Never'}</TableCell>
                      <TableCell>
                        <div className="flex space-x-2" onClick={(e) => e.stopPropagation()}>
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={(e) => {
                              e.stopPropagation();
                              openEditDialog(user);
                            }}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={(e) => {
                              e.stopPropagation();
                              openDeleteDialog(user);
                            }}
                          >
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-8 text-center">
              <AlertCircle className="h-10 w-10 text-muted-foreground mb-4" />
              <h3 className="text-lg font-medium">No users found</h3>
              <p className="text-sm text-muted-foreground mt-1">
                {searchTerm ? 'Try adjusting your search term' : 'No user data available'}
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add User Dialog */}
      <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add New User</DialogTitle>
            <DialogDescription>
              Create a new user account with specified role and permissions.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                value={newUserData.email}
                onChange={(e) => setNewUserData({...newUserData, email: e.target.value})}
                placeholder="Enter email address"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                value={newUserData.password}
                onChange={(e) => setNewUserData({...newUserData, password: e.target.value})}
                placeholder="Enter secure password"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="username">Username (Optional)</Label>
              <Input
                id="username"
                value={newUserData.username}
                onChange={(e) => setNewUserData({...newUserData, username: e.target.value})}
                placeholder="Enter username"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="role">Role</Label>
              <Select 
                value={newUserData.role} 
                onValueChange={(value) => setNewUserData({...newUserData, role: value as 'admin' | 'user'})}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select role" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="user">User</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsAddDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleAddUser} disabled={loading}>
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Create User
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit User</DialogTitle>
            <DialogDescription>
              Update user details and permissions.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Email</Label>
              <p className="text-sm text-muted-foreground">{selectedUser?.email}</p>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="username">Username</Label>
              <Input
                id="username"
                value={selectedUser?.username || ''}
                onChange={(e) => setSelectedUser(selectedUser ? {...selectedUser, username: e.target.value} : null)}
                placeholder="Enter username"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="role">Role</Label>
              <Select 
                value={selectedUser?.role || 'user'} 
                onValueChange={(value) => setSelectedUser(selectedUser ? {...selectedUser, role: value as 'admin' | 'user'} : null)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select role" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="user">User</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleEditUser} disabled={loading}>
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Update User
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete User Confirmation Dialog */}
      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete User</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this user? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <p>You are about to delete the following user:</p>
            <p className="font-medium mt-2">{selectedUser?.username || 'Unknown'}</p>
            <p className="text-sm text-muted-foreground">{selectedUser?.email}</p>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDeleteDialogOpen(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDeleteUser} disabled={loading}>
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Delete User
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Invite User Dialog */}
      <Dialog open={isInviteDialogOpen} onOpenChange={setIsInviteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Invite User</DialogTitle>
            <DialogDescription>
              Send an invitation to a new user with optional expiry date.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="inviteEmail">Email</Label>
              <Input
                id="inviteEmail"
                value={inviteFormData.email}
                onChange={(e) => setInviteFormData({...inviteFormData, email: e.target.value})}
                placeholder="Enter email address"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="inviteRole">Role</Label>
              <Select 
                value={inviteFormData.role} 
                onValueChange={(value) => setInviteFormData({...inviteFormData, role: value as 'admin' | 'user'})}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select role" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="user">User</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="flex items-center space-x-2">
              <Checkbox 
                id="hasExpiry" 
                checked={hasExpiryDate}
                onCheckedChange={(checked) => setHasExpiryDate(checked === true)}
              />
              <Label htmlFor="hasExpiry">Set expiry date for invitation</Label>
            </div>
            
            {hasExpiryDate && (
              <div className="space-y-2">
                <Label htmlFor="expiryDate">Expiry Date</Label>
                <DatePicker 
                  date={expiryDate} 
                  setDate={setExpiryDate}
                  className="w-full"
                />
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsInviteDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleInviteUser} disabled={loading}>
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Send Invitation
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UserManagement; 